<template>
  <!-- when you connect the actual PO data, the only thing we need to change is poGroups()—the rest of the UI and selection logic can remain the same. -->
  <v-row justify="center">
    <!-- =========================================================
         MAIN SERIAL SELECTOR DIALOG
    ========================================================== -->
    <v-dialog
      origin="top center"
      v-model="dialogStatus"
      persistent
      content-class="serial-selector-dialog"
      :max-width="dialogWidth"
    >
      <v-card>
        <!-- Header -->
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text d-flex align-center justify-space-between w-100"
        >
          <div class="d-flex align-center">
            <span class="font-weight-700">
              Select Serial :
              {{ transactionDetail?.product || "Product Information" }}

              <span class="cyan--text" v-if="transactionDetail?.barcode">
                ({{ transactionDetail.barcode }})
              </span>
            </span>
          </div>

          <v-btn icon small @click="$emit('close:dialog')">
            <v-icon color="#000"> mdi-close </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="px-6 py-4">
          <!-- =====================================================
               STATS
          ====================================================== -->
          <v-row class="mb-2">
            <!-- Total Available -->
            <v-col cols="12" sm="6" md="3" class="py-1 px-2">
              <div
                class="stat-card stat-blue d-flex align-center pa-3 serial-tab"
                :class="{
                  'serial-tab-active': activeSerialTab === 'available',
                }"
                role="tab"
                :aria-selected="activeSerialTab === 'available'"
                tabindex="0"
                @click="activeSerialTab = 'available'"
                @keydown.enter="activeSerialTab = 'available'"
              >
                <div
                  class="stat-icon-wrap stat-icon-blue mr-3 d-flex align-center justify-center"
                >
                  <v-icon color="#2563eb" size="20"> mdi-cube-outline </v-icon>
                </div>

                <div>
                  <div class="stat-label">Total Available</div>

                  <div class="stat-val stat-val-blue font-weight-700">
                    <v-progress-circular
                      v-if="serialLoading"
                      indeterminate
                      color="#0284c7"
                      size="18"
                      width="2"
                    />
                    <template v-else>
                      {{ totalAvailableCount }}
                    </template>
                  </div>
                </div>
              </div>
            </v-col>

            <!-- Selected -->
            <v-col cols="12" sm="6" md="3" class="py-1 px-2">
              <div
                class="stat-card stat-green d-flex align-center pa-3 serial-tab"
                :class="{
                  'serial-tab-active': activeSerialTab === 'selected',
                }"
                role="tab"
                :aria-selected="activeSerialTab === 'selected'"
                tabindex="0"
                @click="activeSerialTab = 'selected'"
                @keydown.enter="activeSerialTab = 'selected'"
              >
                <div
                  class="stat-icon-wrap stat-icon-green mr-3 d-flex align-center justify-center"
                >
                  <v-icon color="#16a34a" size="20">
                    mdi-check-circle-outline
                  </v-icon>
                </div>

                <div>
                  <div class="stat-label">Selected</div>

                  <div class="stat-val stat-val-green font-weight-700">
                    {{ enities?.length || 0 }}
                  </div>
                </div>
              </div>
            </v-col>

            <!-- Required -->
            <v-col cols="12" sm="6" md="3" class="py-1 px-2">
              <div class="stat-card stat-purple d-flex align-center pa-3">
                <div
                  class="stat-icon-wrap stat-icon-purple mr-3 d-flex align-center justify-center"
                >
                  <v-icon color="#9333ea" size="20">
                    mdi-clipboard-text-outline
                  </v-icon>
                </div>

                <div>
                  <div class="stat-label">Required / Max</div>

                  <div class="stat-val stat-val-purple font-weight-700">
                    {{ requiredQty }}
                  </div>
                </div>
              </div>
            </v-col>

            <!-- Progress -->
            <v-col cols="12" sm="6" md="3" class="py-1 px-2">
              <div class="stat-card stat-gray d-flex align-center pa-3">
                <div
                  class="stat-progress-circle mr-3 d-flex align-center justify-center"
                >
                  <span class="font-size-11 font-weight-700">
                    {{ progressPercent }}%
                  </span>
                </div>

                <div>
                  <div class="stat-label">Progress</div>

                  <div
                    class="stat-progress-text"
                    :class="
                      isProgressComplete
                        ? 'text-success-custom'
                        : 'text-danger-custom'
                    "
                  >
                    {{ enities?.length || 0 }} of {{ requiredQty }} selected
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- =====================================================
               WARNING
          ====================================================== -->
          <div
            v-if="unavailableCount > 0"
            class="warning-banner d-flex align-center justify-space-between px-4 py-2 mb-3"
          >
            <div class="d-flex align-center">
              <v-icon color="#d97706" class="mr-2" size="20">
                mdi-alert
              </v-icon>

              <span class="warning-text font-weight-600 font-size-13">
                {{ unavailableCount }} serial number(s) are unavailable.

                <span class="font-weight-400 grey--text text--darken-2">
                  Only available serial numbers can be selected.
                </span>
              </span>
            </div>
          </div>

          <!-- =====================================================
               RESERVATION WARNING BANNER
          ====================================================== -->
          <div
            v-if="apiOtherReservedQty > 0 && maxAllowedWithReservation !== null"
            class="warning-banner d-flex align-center justify-space-between px-4 py-2 mb-3"
            style="background: #fffbeb; border: 1px solid #fef3c7;"
          >
            <div class="d-flex align-center">
              <v-icon color="#d97706" class="mr-2" size="20">
                mdi-lock-outline
              </v-icon>

              <span class="warning-text font-weight-600 font-size-13" style="color: #92400e;">
                {{ apiOtherReservedQty }} serial number(s) are reserved for other visit(s)<template v-if="apiOtherReservedPos"> from Purchase Order ({{ apiOtherReservedPos }})</template>.
                <span class="font-weight-400 grey--text text--darken-2 ml-1">
                  (Max selectable: {{ maxAllowedWithReservation }})
                </span>
              </span>
            </div>
          </div>

          <!-- =====================================================
               TOOLBAR
          ====================================================== -->
          <div
            class="d-flex flex-wrap align-center justify-space-between my-3 px-1"
          >
            <div class="d-flex align-center">
              <v-checkbox
                :input-value="isAllRequiredSelected"
                @change="toggleAutoSelect"
                color="cyan darken-2"
                hide-details
                class="mt-0 pt-0 mr-2"
              />

              <div>
                <div
                  class="font-weight-600 font-size-14 text-dark cursor-pointer user-select-none"
                  @click="toggleAutoSelect"
                >
                  Auto Select Available
                </div>

                <div class="caption grey--text">
                  Selects up to {{ requiredQty }} available serial number(s).
                </div>
              </div>
            </div>

            <div class="d-flex align-center flex-wrap mt-2 mt-sm-0">
              <v-btn
                v-if="poGroups.length > 0 || (showGeneralSerials && generalSerials.length > 0)"
                text
                small
                color="cyan darken-2"
                class="font-weight-600 px-2 mr-2 text-capitalize"
                @click="toggleAllSections"
              >
                <v-icon small class="mr-1">
                  {{ isAllCollapsed ? "mdi-arrow-expand-vertical" : "mdi-arrow-collapse-vertical" }}
                </v-icon>
                {{ isAllCollapsed ? "Expand All" : "Collapse All" }}
              </v-btn>

              <div style="min-width: 280px">
                <v-text-field
                  v-model.trim="search"
                  :loading="serialLoading"
                  @keydown.enter="get_numbers()"
                  @keydown.tab="get_numbers()"
                  v-on:click:clear="(search = null), get_numbers()"
                  clearable
                  dense
                  outlined
                  solo
                  flat
                  hide-details
                  placeholder="Search Serial Number..."
                  prepend-inner-icon="mdi-magnify"
                  color="cyan"
                  class="search-input"
                />
              </div>
            </div>
          </div>

          <v-progress-linear
            v-if="serialLoading"
            indeterminate
            color="cyan"
            height="3"
            class="mb-3 rounded"
          ></v-progress-linear>

          <!-- =====================================================
               STATIC PO-WISE SERIAL GROUPING
          ====================================================== -->
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll"
            style="max-height: 52vh; position: relative"
          >
            <!-- Loading State -->
            <div v-if="serialLoading" class="text-center py-12">
              <v-progress-circular
                indeterminate
                color="cyan darken-2"
                size="44"
                width="4"
                class="mb-3"
              />
              <p class="m-0 font-weight-500 font-size-15 grey--text text--darken-1">
                Loading serial numbers...
              </p>
            </div>

            <template v-else>
              <!-- No PO / empty -->
              <div
                v-if="!poGroups.length && (!showGeneralSerials || !generalSerials.length)"
                class="text-center py-12"
              >
                <img
                  height="48"
                  width="48"
                  :src="$assetURL('media/error/empty.png')"
                  class="mb-3 opacity-60"
                />

                <p
                  class="m-0 font-weight-500 font-size-15 grey--text text--darken-1"
                >
                  Uhh... There are no serial numbers at the moment.
                </p>
              </div>

            <!-- PO Groups -->
            <div v-for="po in poGroups" :key="po.id" class="po-group mb-4">
              <!-- =================================================
                   PO HEADER
              ================================================== -->
              <div
                class="po-group-header pa-3 cursor-pointer"
                :class="{ 'is-collapsed': isPoCollapsed(po.id) }"
                @click="togglePoGroup(po.id)"
              >
                <div
                  class="d-flex flex-wrap align-center justify-space-between"
                >
                  <!-- PO Information -->
                  <div class="d-flex align-start">
                    <div class="po-icon mr-3">
                      <v-icon color="#24326d" size="20">
                        mdi-file-document-outline
                      </v-icon>
                    </div>

                    <div>
                      <div class="d-flex align-center flex-wrap">
                        <span class="po-number">
                          {{ po.po_number }}
                        </span>

                        <v-chip
                          x-small
                          outlined
                          color="cyan darken-2"
                          class="ml-2 font-weight-600"
                        >
                          {{ po.serials.length }} Available
                        </v-chip>

                        <v-chip
                          v-if="Number(po.allocated_qty) > 0"
                          x-small
                          color="cyan lighten-4"
                          class="cyan--text text--darken-3 ml-2 font-weight-600"
                        >
                          Allocated: {{ po.allocated_qty }}
                        </v-chip>
                      </div>

                      <div class="po-meta mt-1">
                        <span>
                          <v-icon x-small class="mr-1"> mdi-domain </v-icon>
                          {{ po.supplier }}
                        </span>

                        <span class="mx-2" v-if="po.po_date"> • </span>

                        <span v-if="po.po_date">
                          <v-icon x-small class="mr-1">
                            mdi-calendar-outline
                          </v-icon>
                          {{ formatDate(po.po_date) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- PO Selected Count & Expand Chevron -->
                  <div class="d-flex align-center mt-2 mt-sm-0">
                    <div class="po-selection-summary mr-2">
                      <v-icon small color="cyan darken-2" class="mr-1">
                        mdi-check-circle-outline
                      </v-icon>

                      <span class="font-weight-700">
                        {{ getPoSelectedCount(po) }}
                      </span>

                      <span class="grey--text mx-1"> / </span>

                      <span> {{ po.allocated_qty || po.serials.length }} selected </span>
                    </div>

                    <v-btn icon small class="expand-btn" @click.stop="togglePoGroup(po.id)">
                      <v-icon size="22" color="#64748b">
                        {{ isPoCollapsed(po.id) ? "mdi-chevron-down" : "mdi-chevron-up" }}
                      </v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- =================================================
                     PO DETAILS
                ================================================== -->
                <div class="po-details mt-3" v-if="false">
                  <div class="po-detail-item">
                    <span class="po-detail-label"> Ordered </span>

                    <span class="po-detail-value">
                      {{ po.ordered_qty }}
                    </span>
                  </div>

                  <div class="po-detail-divider"></div>

                  <div class="po-detail-item">
                    <span class="po-detail-label"> Received </span>

                    <span class="po-detail-value">
                      {{ po.received_qty }}
                    </span>
                  </div>

                  <div class="po-detail-divider"></div>

                  <div class="po-detail-item">
                    <span class="po-detail-label"> Available </span>

                    <span class="po-detail-value cyan--text text--darken-3">
                      {{ po.serials.length }}
                    </span>
                  </div>

                  <div class="po-detail-divider"></div>

                  <div class="po-detail-item">
                    <span class="po-detail-label"> Selected </span>

                    <span class="po-detail-value green--text text--darken-2">
                      {{ getPoSelectedCount(po) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- =================================================
                   PO SERIALS
              ================================================== -->
              <v-expand-transition>
                <div v-show="!isPoCollapsed(po.id)">
                  <div v-if="po.serials.length" class="po-serial-body pa-2">
                    <v-row>
                      <v-col
                        v-for="row in po.serials"
                        :key="row.id"
                        cols="12"
                        sm="6"
                        md="3"
                        class="pa-2"
                      >
                        <div
                          class="serial-card pa-3"
                          :class="{
                            'serial-card-selected': isSelected(row.id),
                          }"
                          @click="selecteData(row, po)"
                        >
                          <!-- Serial Header -->
                          <div
                            class="d-flex align-center justify-space-between mb-3"
                          >
                            <div class="d-flex align-center text-truncate mr-1">
                              <span
                                class="serial-text font-weight-700 font-size-14 mr-1 text-truncate"
                              >
                                {{ row.text }}
                              </span>

                              <v-btn
                                icon
                                x-small
                                class="copy-btn ml-1"
                                @click.stop="copyNumber(row.text)"
                                title="Copy Serial Number"
                              >
                                <v-icon size="14" color="grey darken-1">
                                  mdi-content-copy
                                </v-icon>
                              </v-btn>
                            </div>

                            <v-btn
                              icon
                              x-small
                              class="qr-btn"
                              @click.stop="openBarcodeDialog(row)"
                              title="View Barcode / QR"
                            >
                              <v-icon size="20" color="#0891b2">
                                mdi-qrcode-scan
                              </v-icon>
                            </v-btn>
                          </div>

                          <!-- Serial Information -->
                          <div class="serial-info-row mb-3">
                            <div class="serial-info-item">
                              <v-icon x-small color="grey darken-1" class="mr-1">
                                mdi-package-variant-closed
                              </v-icon>

                              <span>
                                {{ po.po_number }}
                              </span>
                            </div>
                          </div>

                          <!-- Bottom -->
                          <div class="d-flex align-center justify-space-between">
                            <span class="in-stock-badge"> In Stock </span>

                            <v-icon
                              v-if="isSelected(row.id)"
                              size="20"
                              color="#0891b2"
                            >
                              mdi-checkbox-marked-circle
                            </v-icon>

                            <v-icon v-else size="20" color="#94a3b8">
                              mdi-checkbox-blank-circle-outline
                            </v-icon>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- No serial in PO -->
                  <div v-else class="text-center grey--text py-4">
                    No serial numbers available for this PO.
                  </div>
                </div>
              </v-expand-transition>
            </div>

            <!-- =====================================================
                 GENERAL / OTHER AVAILABLE SERIALS (BOTTOM SECTION)
            ====================================================== -->
            <div
              v-if="showGeneralSerials && generalSerials.length"
              class="general-serial-group mt-4 mb-4"
            >
              <div
                class="po-group-header pa-3 grey lighten-4 cursor-pointer"
                :class="{ 'is-collapsed': isGeneralCollapsed }"
                @click="toggleGeneralSection"
              >
                <div
                  class="d-flex flex-wrap align-center justify-space-between"
                >
                  <div class="d-flex align-center">
                    <div class="po-icon mr-3">
                      <v-icon color="#475569" size="20">
                        mdi-warehouse
                      </v-icon>
                    </div>

                    <div>
                      <div class="d-flex align-center flex-wrap">
                        <span class="po-number">
                          General Available Stock / Other Serials
                        </span>

                        <v-chip
                          x-small
                          outlined
                          color="blue-grey darken-1"
                          class="ml-2 font-weight-600"
                        >
                          {{ generalSerials.length }} Available
                        </v-chip>

                        <v-chip
                          v-if="apiTotalPoAllocatedQty > 0"
                          x-small
                          color="blue-grey lighten-4"
                          class="blue-grey--text text--darken-3 ml-2 font-weight-600"
                        >
                          Allowed: {{ maxGeneralAllowed }}
                        </v-chip>
                      </div>

                      <div class="po-meta mt-1">
                        <span class="caption grey--text text--darken-1">
                          Available inventory not reserved for specific Purchase Orders
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex align-center mt-2 mt-sm-0">
                    <div class="po-selection-summary mr-2">
                      <v-icon small color="cyan darken-2" class="mr-1">
                        mdi-check-circle-outline
                      </v-icon>

                      <span class="font-weight-700">
                        {{ getGeneralSelectedCount() }}
                      </span>

                      <template v-if="apiTotalPoAllocatedQty > 0">
                        <span class="grey--text mx-1"> / </span>
                        <span> {{ maxGeneralAllowed }} selected </span>
                      </template>
                      <template v-else>
                        <span class="grey--text mx-1"> selected </span>
                      </template>
                    </div>

                    <v-btn icon small class="expand-btn" @click.stop="toggleGeneralSection">
                      <v-icon size="22" color="#64748b">
                        {{ isGeneralCollapsed ? "mdi-chevron-down" : "mdi-chevron-up" }}
                      </v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <!-- General Serials Body -->
              <v-expand-transition>
                <div v-show="!isGeneralCollapsed" class="po-serial-body pa-2">
                  <v-row>
                    <v-col
                      v-for="row in generalSerials"
                      :key="row.id"
                      cols="12"
                      sm="6"
                      md="3"
                      class="pa-2"
                    >
                      <div
                        class="serial-card pa-3"
                        :class="{
                          'serial-card-selected': isSelected(row.id),
                        }"
                        @click="selecteData(row, null)"
                      >
                        <!-- Serial Header -->
                        <div
                          class="d-flex align-center justify-space-between mb-3"
                        >
                          <div class="d-flex align-center text-truncate mr-1">
                            <span
                              class="serial-text font-weight-700 font-size-14 mr-1 text-truncate"
                            >
                              {{ row.text }}
                            </span>

                            <v-btn
                              icon
                              x-small
                              class="copy-btn ml-1"
                              @click.stop="copyNumber(row.text)"
                              title="Copy Serial Number"
                            >
                              <v-icon size="14" color="grey darken-1">
                                mdi-content-copy
                              </v-icon>
                            </v-btn>
                          </div>

                          <v-btn
                            icon
                            x-small
                            class="qr-btn"
                            @click.stop="openBarcodeDialog(row)"
                            title="View Barcode / QR"
                          >
                            <v-icon size="20" color="#0891b2">
                              mdi-qrcode-scan
                            </v-icon>
                          </v-btn>
                        </div>

                        <!-- Serial Information -->
                        <div class="serial-info-row mb-3">
                          <div class="serial-info-item">
                            <v-icon x-small color="grey darken-1" class="mr-1">
                              mdi-cube-outline
                            </v-icon>

                            <span>
                              General Stock
                            </span>
                          </div>
                        </div>

                        <!-- Bottom -->
                        <div class="d-flex align-center justify-space-between">
                          <span class="in-stock-badge"> In Stock </span>

                          <v-icon
                            v-if="isSelected(row.id)"
                            size="20"
                            color="#0891b2"
                          >
                            mdi-checkbox-marked-circle
                          </v-icon>

                          <v-icon v-else size="20" color="#94a3b8">
                            mdi-checkbox-blank-circle-outline
                          </v-icon>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>
            </div>
          </template>
        </perfect-scrollbar>
        </v-card-text>

        <!-- =======================================================
             FOOTER
        ======================================================== -->
        <v-divider class="my-0"></v-divider>

        <v-card-actions class="px-6 py-3">
          <div class="d-flex align-center">
            <v-icon color="cyan" size="22" class="mr-2">
              mdi-check-circle
            </v-icon>

            <div>
              <div class="font-weight-700 font-size-14 custom-header-blue-text">
                {{ enities.length }} Selected
              </div>

              <div class="caption grey--text">
                {{
                  enities.length === 0
                    ? "No serial numbers selected"
                    : `${enities.length} of ${requiredQty} selected`
                }}
              </div>
            </div>
          </div>

          <v-spacer></v-spacer>

          <v-btn
            depressed
            :disabled="!isSaveEnabled || serialLoading"
            :loading="serialLoading"
            class="mx-2 custom-bold-button white--text"
            color="cyan"
            @click="onDetailSubmit()"
          >
            Save
          </v-btn>

          <v-btn
            depressed
            :disabled="serialLoading"
            class="mx-2 custom-grey-border custom-bold-button"
            @click="$emit('close:dialog')"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- =========================================================
         BARCODE / QR POPUP
    ========================================================== -->
    <v-dialog
      v-model="barcodeDialog"
      max-width="420"
      persistent
      content-class="barcode-popup-dialog"
    >
      <v-card>
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text d-flex align-center justify-space-between w-100"
        >
          <span class="font-weight-700"> Barcode </span>

          <v-btn icon small @click="barcodeDialog = false">
            <v-icon color="#000"> mdi-close </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="px-6 py-4">
          <div
            class="barcode-preview-box pa-6 d-flex flex-column align-center justify-center"
          >
            <img
              v-if="selectedRowBarcodeUrl"
              :src="selectedRowBarcodeUrl"
              style="max-width: 220px; max-height: 220px; object-fit: contain"
              alt="Barcode/QR"
            />

            <div v-else class="text-center py-6">
              <v-icon size="96" color="#334155"> mdi-qrcode </v-icon>
            </div>

            <div class="serial-pill-badge mt-4">
              {{ selectedRowSerialText }}
            </div>
          </div>
        </v-card-text>

        <v-divider class="my-0"></v-divider>

        <v-card-actions class="px-4 py-2">
          <v-spacer></v-spacer>

          <v-btn
            depressed
            class="mx-2 custom-grey-border custom-bold-button"
            @click="barcodeDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>
<script>
import { QUERY } from "@/core/services/store/request.module";
import CommonMixin from "@/core/plugins/common-mixin";
import { mapGetters } from "vuex";

import {
  ErrorEventBus,
  InitializeError,
  SuccessEventBus,
} from "@/core/lib/message.lib";

export default {
  name: "Serial-Number-Select",

  mixins: [CommonMixin],

  props: {
    type: {
      type: String,
      default: null,
    },

    dialogStatus: {
      type: Boolean,
      default: false,
    },

    transactionDetail: {
      type: Object,
      default: () => ({}),
    },

    parentId: {
      type: Number,
      default: 0,
    },

    parentIndex: {
      type: Number,
      default: 0,
    },

    allowPartial: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    dialogStatus: {
      deep: true,
      immediate: true,

      handler(param) {
        if (param) {
          this.selectionInitialized = false;
          this.enities = [];
          this.activeSerialTab = "available";
          this.autoSelectActive = false;
          this.collapsedPoGroups = [];
          this.isGeneralCollapsed = false;
          this.apiOtherReservedQty = 0;
          this.apiOtherReservedPos = "";
          this.apiMaxSelectable = null;
          this.get_numbers();
        }
      },
    },
  },

  data() {
    return {
      search: null,

      serialLoading: false,

      serialNumber: [],

      enities: [],

      selectionInitialized: false,

      autoSelectActive: false,

      barcodeDialog: false,

      selectedRowBarcodeUrl: null,

      selectedRowSerialText: null,

      unavailableCount: 0,

      activeSerialTab: "available",

      /*
       * =========================================================
       * DYNAMIC PO & GENERAL SERIALS CONFIGURATION
       * =========================================================
       */
      apiPoGroups: [],

      apiGeneralSerials: [],

      apiTotalPoAllocatedQty: 0,

      apiShowGeneralSerials: true,

      apiOtherReservedQty: 0,

      apiOtherReservedPos: "",

      apiMaxSelectable: null,

      collapsedPoGroups: [],

      isGeneralCollapsed: false,
    };
  },

  computed: {
    ...mapGetters(["vSelectedLineItem"]),

    dialogWidth() {
      return (
        this.lodash.toSafeInteger((document.body.clientWidth / 100) * 70) || 980
      );
    },

    requiredQty() {
      return Number(this.transactionDetail?.quantity || 0);
    },

    progressPercent() {
      if (this.requiredQty <= 0) return 0;

      const pct = Math.round(
        ((this.enities?.length || 0) / this.requiredQty) * 100
      );

      return Math.min(pct, 100);
    },

    isProgressComplete() {
      return (
        (this.enities?.length || 0) === this.requiredQty && this.requiredQty > 0
      );
    },

    isAllRequiredSelected() {
      return this.autoSelectActive && this.isProgressComplete;
    },

    isSaveEnabled() {
      if (this.allowPartial) {
        return (this.enities?.length || 0) <= this.requiredQty;
      }

      return (
        (this.enities?.length || 0) === this.requiredQty && this.requiredQty > 0
      );
    },

    totalAvailableCount() {
      const poCount = (this.apiPoGroups || []).reduce(
        (acc, po) => acc + (po.serials?.length || 0),
        0
      );
      const generalCount = this.showGeneralSerials
        ? (this.apiGeneralSerials?.length || 0)
        : 0;
      return poCount + generalCount;
    },

    /*
     * =========================================================
     * SEARCHED SERIALS
     * =========================================================
     */
    filteredSerialNumbers() {
      if (!this.search) {
        return this.serialNumber;
      }

      const q = this.search.toLowerCase();

      return (this.serialNumber || []).filter((s) => {
        return s.text && s.text.toLowerCase().includes(q);
      });
    },

    /*
     * =========================================================
     * PO-WISE DYNAMIC GROUPING
     * =========================================================
     */
    poGroups() {
      const groups = (this.apiPoGroups || []).map((po) => {
        let serials = po.serials || [];
        if (this.search) {
          const q = this.search.toLowerCase();
          serials = serials.filter(
            (s) => s.text && s.text.toLowerCase().includes(q)
          );
        }
        if (this.activeSerialTab === "selected") {
          serials = serials.filter((s) => this.isSelected(s.id));
        }
        return {
          ...po,
          serials,
        };
      });

      return groups.filter(
        (group) =>
          group.serials.length > 0 ||
          (this.activeSerialTab !== "selected" && Number(group.allocated_qty) > 0)
      );
    },

    /*
     * =========================================================
     * GENERAL STOCK SERIALS
     * =========================================================
     */
    generalSerials() {
      let serials = this.apiGeneralSerials || [];
      if (this.search) {
        const q = this.search.toLowerCase();
        serials = serials.filter(
          (s) => s.text && s.text.toLowerCase().includes(q)
        );
      }
      if (this.activeSerialTab === "selected") {
        serials = serials.filter((s) => this.isSelected(s.id));
      }
      return serials;
    },

    /*
     * =========================================================
     * VISIBILITY OF BOTTOM GENERAL SERIALS
     *
     * If requiredQty <= totalPoAllocatedQty -> HIDE bottom section
     * If requiredQty > totalPoAllocatedQty  -> SHOW bottom section
     * =========================================================
     */
    showGeneralSerials() {
      const hasSelectedGeneral = (this.apiGeneralSerials || []).some((s) =>
        this.isSelected(s.id)
      );
      if (hasSelectedGeneral) {
        return true;
      }

      if (!this.apiPoGroups.length) {
        return true;
      }

      if (this.requiredQty > 0 && this.apiTotalPoAllocatedQty > 0) {
        return this.requiredQty > this.apiTotalPoAllocatedQty;
      }

      return this.apiShowGeneralSerials;
    },

    /*
     * =========================================================
     * MAXIMUM ALLOWED GENERAL STOCK SERIALS
     *
     * If PO allocations exist, general stock can only supply
     * (requiredQty - totalPoAllocatedQty).
     * =========================================================
     */
    maxGeneralAllowed() {
      if (this.apiTotalPoAllocatedQty > 0) {
        return Math.max(0, this.requiredQty - this.apiTotalPoAllocatedQty);
      }
      return this.requiredQty;
    },

    /*
     * =========================================================
     * MAXIMUM ALLOWED SERIALS TAKING OTHER RESERVATIONS INTO ACCOUNT
     * =========================================================
     */
    maxAllowedWithReservation() {
      if (this.apiOtherReservedQty > 0 && this.apiMaxSelectable !== null) {
        return Math.max(0, this.apiMaxSelectable);
      }
      return null;
    },

    isAllCollapsed() {
      const allPoCollapsed =
        !this.poGroups.length ||
        this.poGroups.every((po) => this.isPoCollapsed(po.id));
      const generalCollapsed =
        !this.generalSerials.length || this.isGeneralCollapsed;
      return allPoCollapsed && generalCollapsed;
    },
  },

  methods: {
    togglePoGroup(poId) {
      const index = this.collapsedPoGroups.indexOf(poId);
      if (index > -1) {
        this.collapsedPoGroups.splice(index, 1);
      } else {
        this.collapsedPoGroups.push(poId);
      }
    },

    isPoCollapsed(poId) {
      return this.collapsedPoGroups.includes(poId);
    },

    toggleGeneralSection() {
      this.isGeneralCollapsed = !this.isGeneralCollapsed;
    },

    toggleAllSections() {
      if (this.isAllCollapsed) {
        this.collapsedPoGroups = [];
        this.isGeneralCollapsed = false;
      } else {
        this.collapsedPoGroups = this.poGroups.map((p) => p.id);
        this.isGeneralCollapsed = true;
      }
    },

    /*
     * =========================================================
     * SERIAL ID NORMALIZATION
     * =========================================================
     */
    normalizeSerialId(serial) {
      const id =
        serial && typeof serial === "object"
          ? serial.serial_number_id || serial.id
          : serial;

      return Number(id);
    },

    /*
     * =========================================================
     * CHECK SERIAL SELECTION
     * =========================================================
     */
    isSelected(id) {
      return this.enities.includes(this.normalizeSerialId(id));
    },

    /*
     * =========================================================
     * PO SELECTED COUNT
     * =========================================================
     */
    getPoSelectedCount(po) {
      if (!po) {
        return 0;
      }

      const originalPo =
        (this.apiPoGroups || []).find((p) => p.id === po.id) || po;
      const serials = originalPo.serials || [];

      return serials.filter((serial) => this.isSelected(serial.id)).length;
    },

    /*
     * =========================================================
     * GENERAL SELECTED COUNT
     * =========================================================
     */
    getGeneralSelectedCount() {
      const serials = this.apiGeneralSerials || [];
      return serials.filter((serial) => this.isSelected(serial.id)).length;
    },

    /*
     * =========================================================
     * SERIAL SELECTION WITH PER-PO ALLOCATION & GENERAL LIMITS
     * =========================================================
     */
    selecteData(row, po = null) {
      if (!row || !row.id) {
        return;
      }

      const id = this.normalizeSerialId(row);

      const foundIndex = this.enities.findIndex((item) => item === id);

      /*
       * Manual selection disables Auto Select mode.
       */
      this.autoSelectActive = false;

      /*
       * Remove existing selection.
       */
      if (foundIndex !== -1) {
        this.enities.splice(foundIndex, 1);
        return;
      }

      // Check whether this serial belongs to a PO group or General Stock
      let targetPo = po;
      if (!targetPo && this.apiPoGroups.length) {
        targetPo = this.apiPoGroups.find((p) =>
          (p.serials || []).some((s) => this.normalizeSerialId(s) === id)
        );
      }

      /*
       * 1. Global quantity protection.
       */
      if (this.enities.length >= this.requiredQty) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError(
            `You cannot select more than ${this.requiredQty} serial number(s) as entered quantity is ${this.requiredQty}.`
          )
        );

        return;
      }

      /*
       * 2. Check Reservation limit for other visits.
       */
      if (
        this.apiOtherReservedQty > 0 &&
        this.maxAllowedWithReservation !== null &&
        this.enities.length >= this.maxAllowedWithReservation
      ) {
        const poInfo = this.apiOtherReservedPos
          ? ` from Purchase Order (${this.apiOtherReservedPos})`
          : "";
        ErrorEventBus.$emit(
          "update:error",
          InitializeError(
            `You cannot select more than ${this.maxAllowedWithReservation} serial numbers because ${this.apiOtherReservedQty} serial number(s) are reserved for other visit(s)${poInfo}.`
          )
        );
        return;
      }

      /*
       * 3. Check per-PO allocation quota limit if serial is from a PO group
       */
      if (targetPo) {
        const currentPoSelected = this.getPoSelectedCount(targetPo);
        if (Number(targetPo.allocated_qty) > 0) {
          const maxPoAllocated =
            targetPo.max_selectable !== undefined && targetPo.max_selectable !== null
              ? Math.min(Number(targetPo.allocated_qty), Number(targetPo.max_selectable))
              : Number(targetPo.allocated_qty);

          if (currentPoSelected >= maxPoAllocated) {
            ErrorEventBus.$emit(
              "update:error",
              InitializeError(
                `You can only select up to ${maxPoAllocated} serial number(s) allocated from Purchase Order (${targetPo.po_number || "PO"}).`
              )
            );
            return;
          }
        } else if (targetPo.max_selectable !== undefined && targetPo.max_selectable !== null) {
          if (currentPoSelected >= Number(targetPo.max_selectable)) {
            const resInfo = targetPo.reserved_qty
              ? ` because ${targetPo.reserved_qty} serial number(s) are reserved for other visit(s)`
              : "";
            ErrorEventBus.$emit(
              "update:error",
              InitializeError(
                `You cannot select more than ${targetPo.max_selectable} serial numbers from Purchase Order (${targetPo.po_number || "PO"})${resInfo}.`
              )
            );
            return;
          }
        }
      }

      /*
       * 4. Check General Stock quota limit if serial is from general stock
       */
      if (!targetPo && this.apiTotalPoAllocatedQty > 0) {
        const currentGeneralSelected = this.getGeneralSelectedCount();
        if (currentGeneralSelected >= this.maxGeneralAllowed) {
          ErrorEventBus.$emit(
            "update:error",
            InitializeError(
              `You can only select up to ${this.maxGeneralAllowed} serial number(s) from general stock because ${this.apiTotalPoAllocatedQty} are allocated from Purchase Order(s).`
            )
          );
          return;
        }
      }

      /*
       * Add serial.
       */
      this.enities.push(id);
    },

    /*
     * =========================================================
     * AUTO SELECT TOGGLE
     * =========================================================
     */
    toggleAutoSelect() {
      if (this.autoSelectActive) {
        this.enities = [];
        this.autoSelectActive = false;
      } else {
        this.autoSelect();
      }
    },

    /*
     * =========================================================
     * SMART AUTO SELECT (PO Allocations first, then General)
     * =========================================================
     */
    autoSelect() {
      if (this.requiredQty <= 0) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Please enter a valid quantity greater than 0.")
        );

        return;
      }

      const selected = [];
      const effectiveMax =
        this.maxAllowedWithReservation !== null
          ? Math.min(this.requiredQty, this.maxAllowedWithReservation)
          : this.requiredQty;

      // 1. Pick from PO Groups up to each PO's allocated_qty
      const poGroupsSource = this.apiPoGroups.length
        ? this.apiPoGroups
        : this.poGroups;

      for (const po of poGroupsSource) {
        if (selected.length >= effectiveMax) break;
        const maxPoPick =
          Number(po.allocated_qty) > 0
            ? (po.max_selectable !== undefined && po.max_selectable !== null
                ? Math.min(Number(po.allocated_qty), Number(po.max_selectable))
                : Number(po.allocated_qty))
            : (po.max_selectable !== undefined && po.max_selectable !== null
                ? Number(po.max_selectable)
                : (po.serials || []).length);
        let pickedFromPo = 0;
        for (const serial of po.serials || []) {
          if (selected.length >= effectiveMax) break;
          if (maxPoPick > 0 && pickedFromPo >= maxPoPick) break;
          const serialId = this.normalizeSerialId(serial);
          if (!selected.includes(serialId)) {
            selected.push(serialId);
            pickedFromPo++;
          }
        }
      }

      // 2. If more are needed and showGeneralSerials is true, pick from generalSerials
      if (selected.length < effectiveMax && this.showGeneralSerials) {
        const generalSource = this.apiGeneralSerials.length
          ? this.apiGeneralSerials
          : this.generalSerials;

        let pickedGeneral = 0;
        for (const serial of generalSource) {
          if (selected.length >= effectiveMax) break;
          if (
            this.apiTotalPoAllocatedQty > 0 &&
            pickedGeneral >= this.maxGeneralAllowed
          ) {
            break;
          }
          const serialId = this.normalizeSerialId(serial);
          if (!selected.includes(serialId)) {
            selected.push(serialId);
            pickedGeneral++;
          }
        }
      }

      this.enities = selected;
      this.autoSelectActive = this.enities.length === this.requiredQty;

      if (this.enities.length === 0) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("No serial numbers available to select.")
        );
      } else if (
        this.maxAllowedWithReservation !== null &&
        this.enities.length < this.requiredQty &&
        this.enities.length >= this.maxAllowedWithReservation
      ) {
        const poInfo = this.apiOtherReservedPos
          ? ` from Purchase Order (${this.apiOtherReservedPos})`
          : "";
        ErrorEventBus.$emit(
          "update:warning",
          `Only ${this.enities.length} serial numbers could be selected because ${this.apiOtherReservedQty} serial number(s) are reserved for other visit(s)${poInfo}.`
        );
      } else if (this.enities.length < this.requiredQty) {
        ErrorEventBus.$emit(
          "update:warning",
          `Only ${this.enities.length} serial numbers could be selected based on allocation limits.`
        );
      }
    },

    /*
     * =========================================================
     * COPY SERIAL
     * =========================================================
     */
    copyNumber(data) {
      if (!data) {
        return;
      }

      navigator.clipboard.writeText(data);

      SuccessEventBus.$emit("update:success", `${data} successfully copied.`);
    },

    /*
     * =========================================================
     * BARCODE
     * =========================================================
     */
    openBarcodeDialog(row) {
      this.selectedRowSerialText = row.text || "N/A";

      this.selectedRowBarcodeUrl = row.barcode_image || null;

      this.barcodeDialog = true;
    },

    /*
     * =========================================================
     * SAVE
     * =========================================================
     */
    onDetailSubmit() {
      if (!this.allowPartial && this.enities.length !== this.requiredQty) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError(
            `Please select exactly ${this.requiredQty} serial number(s). Currently selected: ${this.enities.length}.`
          )
        );

        return false;
      }

      if (this.allowPartial && this.enities.length > this.requiredQty) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError(
            `You cannot select more than ${this.requiredQty} serial number(s). Currently selected: ${this.enities.length}.`
          )
        );

        return false;
      }

      if (
        this.apiOtherReservedQty > 0 &&
        this.maxAllowedWithReservation !== null &&
        this.enities.length > this.maxAllowedWithReservation
      ) {
        const poInfo = this.apiOtherReservedPos
          ? ` from Purchase Order (${this.apiOtherReservedPos})`
          : "";
        ErrorEventBus.$emit(
          "update:error",
          InitializeError(
            `You cannot select more than ${this.maxAllowedWithReservation} serial numbers because ${this.apiOtherReservedQty} serial number(s) are reserved for other visit(s)${poInfo}.`
          )
        );

        return false;
      }

      /*
       * Existing parent payload preserved.
       */
      const payload = {
        data: this.enities,

        parent: this.transactionDetail.product_id,

        index: this.parentIndex,
      };

      /*
       * Existing event preserved.
       */
      this.$emit("update:item", payload);
    },

    /*
     * =========================================================
     * GET SERIAL NUMBERS
     * =========================================================
     */
    get_numbers() {
      /*
       * Serial-number searches can overlap.
       * Only hydrate selection from line item once.
       */
      const shouldInitializeSelection = !this.selectionInitialized;

      this.selectionInitialized = true;

      this.serialLoading = true;

      const isDuplicate = Boolean(this.$route?.query?.duplicate);

      const visitId = isDuplicate
        ? 0
        : (this.transactionDetail?.visit_id ||
           this.transactionDetail?.visit ||
           this.$route?.query?.visit ||
           this.$route?.params?.visit ||
           this.transactionDetail?.parent ||
           0);

      const lineItemId = isDuplicate ? 0 : (this.transactionDetail?.id || 0);
      const requiredQty = Number(
        this.transactionDetail?.quantity || this.requiredQty || 0
      );

      const productId =
        this.transactionDetail?.product_id ||
        this.transactionDetail?.product?.id ||
        (typeof this.transactionDetail?.product === "number"
          ? this.transactionDetail.product
          : 0) ||
        this.parentId ||
        0;

      this.$store
        .dispatch(QUERY, {
          url: `serial-number/${productId}`,

          data: {
            search: this.search,
            visit: visitId,
            line_item: lineItemId,
            required_qty: requiredQty,
          },
        })

        .then((response) => {
          this.serialNumber = response?.data?.rows || [];
          this.apiPoGroups = Array.isArray(response?.data?.po_groups)
            ? response.data.po_groups
            : [];
          this.apiGeneralSerials = Array.isArray(response?.data?.general_serials)
            ? response.data.general_serials
            : this.apiPoGroups.length
            ? []
            : this.serialNumber;
          this.apiTotalPoAllocatedQty = Number(
            response?.data?.total_po_allocated_qty || 0
          );
          this.apiShowGeneralSerials =
            response?.data?.show_general_serials !== undefined
              ? Boolean(response.data.show_general_serials)
              : true;
          this.apiOtherReservedQty = Number(
            response?.data?.other_reserved_qty || 0
          );
          this.apiOtherReservedPos = String(
            response?.data?.other_reserved_pos || ""
          );
          this.apiMaxSelectable =
            response?.data?.max_selectable !== undefined
              ? Number(response.data.max_selectable)
              : null;

          /*
           * Existing selection initialization preserved.
           */
          if (shouldInitializeSelection) {
            const initialEnities =
              [
                this.transactionDetail?.selected_serial_ids,

                this.transactionDetail?.serialize_number,

                this.transactionDetail?.serialNumber,

                this.transactionDetail?.adjust_serial_array,

                this.vSelectedLineItem?.[this.parentIndex]?.serialNumber,
              ].find((items) => Array.isArray(items) && items.length) || [];

            this.enities = Array.isArray(initialEnities)
              ? initialEnities.map((x) => this.normalizeSerialId(x))
              : [];

            this.activeSerialTab = this.enities.length
              ? "selected"
              : "available";
          }
        })

        .catch((error) => {
          console.error("Error fetching serial numbers:", error);
        })

        .finally(() => {
          this.serialLoading = false;
        });
    },
  },
};
</script>
<style scoped>
/* ============================================================
   DIALOG
============================================================ */

.v-dialog__content {
  align-items: start;
}

/* ============================================================
   STAT CARDS
============================================================ */

.stat-card {
  border-radius: 12px;
  transition: transform 0.15s ease;
}

.serial-tab {
  cursor: pointer;
  outline: none;
}

.serial-tab:hover,
.serial-tab:focus,
.serial-tab-active {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.18);
}

.serial-tab-active.stat-blue {
  border: 2px solid #0284c7;
}

.serial-tab-active.stat-green {
  border: 2px solid #16a34a;
}

.stat-blue {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}

.stat-icon-blue {
  width: 36px;
  height: 36px;
  background: #e0f2fe;
  border-radius: 8px;
}

.stat-val-blue {
  color: #0284c7;
  font-size: 18px;
}

.stat-green {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.stat-icon-green {
  width: 36px;
  height: 36px;
  background: #dcfce7;
  border-radius: 8px;
}

.stat-val-green {
  color: #16a34a;
  font-size: 18px;
}

.stat-purple {
  background: #faf5ff;
  border: 1px solid #e9d5ff;
}

.stat-icon-purple {
  width: 36px;
  height: 36px;
  background: #f3e8ff;
  border-radius: 8px;
}

.stat-val-purple {
  color: #9333ea;
  font-size: 18px;
}

.stat-gray {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.stat-progress-circle {
  width: 36px;
  height: 36px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  color: #64748b;
}

.stat-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.2;
}

.text-success-custom {
  color: #16a34a;
  font-weight: 700;
  font-size: 13px;
}

.text-danger-custom {
  color: #dc2626;
  font-weight: 600;
  font-size: 13px;
}

/* ============================================================
   WARNING
============================================================ */

.warning-banner {
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
}

.warning-text {
  color: #92400e;
}

/* ============================================================
   SEARCH
============================================================ */

.search-input >>> .v-input__control > .v-input__slot {
  border-color: #cbd5e1 !important;
  min-height: 38px !important;
}

/* ============================================================
   PO GROUP
============================================================ */

.po-group {
  border: 1px solid #dbe4ee;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.general-serial-group {
  border: 1px solid #dbe4ee;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.po-group-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.po-group-header:hover {
  background: #f1f5f9;
}

.po-group-header.is-collapsed {
  border-bottom: none;
}

.po-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #eef2ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.po-number {
  color: #24326d;
  font-size: 15px;
  font-weight: 700;
}

.po-meta {
  color: #64748b;
  font-size: 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.po-selection-summary {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #475569;
  background: #ffffff;
  border: 1px solid #dbe4ee;
  border-radius: 6px;
  padding: 5px 9px;
}

/* ============================================================
   PO DETAILS
============================================================ */

.po-details {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
}

.po-detail-item {
  display: flex;
  align-items: center;
  min-width: 105px;
}

.po-detail-label {
  color: #64748b;
  font-size: 11px;
  margin-right: 6px;
}

.po-detail-value {
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}

.po-detail-divider {
  width: 1px;
  height: 20px;
  background: #e2e8f0;
  margin: 0 12px;
}

/* ============================================================
   SERIAL BODY
============================================================ */

.po-serial-body {
  background: #ffffff;
}

/* ============================================================
   SERIAL CARD
============================================================ */

.serial-card {
  background: #f4fbf7;
  border: 1.5px solid #d1fae5;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.serial-card:hover {
  border-color: #059669;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.12);
  transform: translateY(-1px);
}

.serial-card-selected {
  background: #f0fdf4 !important;
  border: 2px solid #0891b2 !important;
  box-shadow: 0 4px 14px rgba(8, 145, 178, 0.18) !important;
}

.serial-text {
  color: #166534;
}

.serial-info-row {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 5px 7px;
}

.serial-info-item {
  display: flex;
  align-items: center;
  color: #64748b;
  font-size: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.in-stock-badge {
  background: #15803d;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}

.copy-btn,
.qr-btn {
  background: transparent;
}

/* ============================================================
   BARCODE MODAL
============================================================ */

.barcode-preview-box {
  background: #ffffff;
  border: 1.5px dashed #cbd5e1;
  border-radius: 16px;
}

.serial-pill-badge {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-weight: 700;
  font-size: 13px;
  border-radius: 20px;
  padding: 4px 22px;
}
</style>
